// ─────────────────────────────────────────────────────────────────────────
// SaaS Summary — synthetic demo fixture
//
// THROWAWAY DEMO DATA. All values are synthetic and scaled to an early
// Series A AI-SaaS profile (Lorikeet-style: hybrid subscription + usage).
// No real client (Fusion SD / Subscript) values appear here.
//
// This module intentionally mirrors the shape a future GET endpoint would
// return (e.g. /api/model-vnext/workspace/<id>/saas-summary/?as_of=YYYY-MM),
// so the tab can swap to a live API later with no component changes.
//
// Each metric carries a `detail` object shaped like the Workbench
// cell-detail response so clicking a KPI / row opens a drill-down Sheet
// with no extra fetch. Metrics are marked "derived" (Pecunio computes them,
// so we show the formula + worked calculation) or "imported" (taken verbatim
// from the source snapshot — we DO NOT fabricate a derivation).
// ─────────────────────────────────────────────────────────────────────────

export type MetricGroup = "growth" | "retention" | "unit_economics" | "efficiency";
export type MetricFormat = "currency" | "percent" | "ratio" | "months" | "count";

/** A single input row feeding a derived metric's calculation. */
export interface MetricInput {
    label: string;
    /** Pre-formatted display value, e.g. "$667K", "112.6%", "94". */
    value: string;
    /** The snapshot row this value came from (imported provenance). */
    sourceLabel?: string;
    /** Period the input value belongs to, e.g. "Aug 2026". */
    sourcePeriod?: string;
    note?: string;
}

/**
 * How a metric's value is produced:
 *  - "derived"  → Pecunio computes it from primitives; show formula + worked calculation.
 *  - "imported" → taken directly from the source snapshot; DO NOT fabricate a derivation.
 */
export type MetricMethod = "imported" | "derived";

/** Drill-down detail for a metric — mirrors the Workbench cell-detail contract. */
export interface MetricDetail {
    /** Plain-English "what this is". */
    definition: string;
    /** Whether Pecunio derives this or imports it verbatim. */
    method: MetricMethod;
    /** Human-readable formula (derived metrics only), e.g. "MRR × 12". */
    formula?: string;
    /** Worked example with substituted values (derived only), e.g. "$667K × 12 = $8.00M". */
    calculation?: string;
    /** The terms feeding this month's number (derived only). */
    inputs?: MetricInput[];
    /** The source-snapshot row this metric maps to (imported only). */
    sourceRow?: string;
    /** Where the underlying data comes from (synthetic for the demo). */
    source: string;
    /** Sanitised import provenance shown in the drawer. */
    importedAt?: string;
    schemaVersion?: number;
    /** Time basis, e.g. "Rolling 12 months (TTM)" or "Current month". */
    basis: string;
    /** Optional benchmark / target context. */
    benchmark?: string;
    /** Interpretation warnings / formula-variant caveats. */
    caveats?: string[];
}

export interface SaaSMetric {
    key: string;
    label: string;
    group: MetricGroup;
    format: MetricFormat;
    /** Raw numeric value (unformatted) for charts / re-formatting. */
    raw: number;
    /** Pre-formatted display value. */
    display: string;
    /** Percentage change vs prior month (signed). */
    deltaPct: number;
    /** Short basis label shown on the card/table, e.g. "vs prior month". */
    basis: string;
    /** True → promote to a headline KPI card. Controls card selection without changing data. */
    headline: boolean;
    /** True → a downward movement is good (churn, CAC, payback, burn). */
    inverse: boolean;
    detail: MetricDetail;
}

export interface ArrBridge {
    begin: number;
    new: number;
    expansion: number;
    contraction: number;
    churn: number;
    end: number;
    /** True when begin + new + expansion − contraction − churn === end. */
    reconciled: boolean;
}

export interface MonthSnapshot {
    asOf: string; // "YYYY-MM"
    label: string; // "Aug 2026"
    previousLabel: string; // "May"
    periodSubtitle: string;
    executiveSummary: {label: string; text: string}[];
    metrics: SaaSMetric[];
    arrBridge: ArrBridge;
}

export interface TrendPoint {
    monthKey: string;
    label: string;
    revenue: number; // total revenue for the month ($K)
    arr: number; // annualised recurring run-rate ($M)
    nrr: number; // % TTM
    grr: number; // % TTM
    customers: number;
}

export interface ScorecardRow {
    metric: string;
    format: MetricFormat;
    values: (number | null)[]; // aligned to scorecardMonths
    inverse?: boolean;
}

export interface SaaSSummaryData {
    source: string;
    availableMonths: {value: string; label: string}[];
    months: Record<string, MonthSnapshot>;
    trend: TrendPoint[];
    scorecardMonths: string[]; // ["Mar 2026", ... "Aug 2026"]
    scorecard: ScorecardRow[];
}

const SYNTHETIC_SOURCE = "Synthetic Subscript Business Snapshot";
const IMPORTED_AT = "18 Sep 2026";
const SCHEMA_VERSION = 1;
const IMPORTED_NOTE = "Imported directly from the source snapshot; no Pecunio transformation applied.";

// ── Helper: default headline/inverse flags ───────────────────────────────

function metric(
    m: Omit<SaaSMetric, "headline" | "inverse"> & {headline?: boolean; inverse?: boolean},
): SaaSMetric {
    return {headline: false, inverse: false, ...m};
}

// ─────────────────────────────────────────────────────────────────────────
// Aug 2026 — the canonical snapshot (detail objects defined once here;
// Jul/Jun inherit them via adjustSnapshot and only override raw/display).
// ─────────────────────────────────────────────────────────────────────────

const AUG_2026: MonthSnapshot = {
    asOf: "2026-08",
    label: "Aug 2026",
    previousLabel: "Jul",
    periodSubtitle: "Board view · Aug 2026 · rolling 12-month metrics where noted",
    executiveSummary: [
        {label: "Growth", text: "ARR reached $8.00M, up 34.6% year on year, with $29K of net-new MRR this month."},
        {label: "Retention", text: "NRR remains healthy at 111.8% and GRR at 94.2%, despite a modest month-on-month softening."},
        {label: "Unit economics", text: "LTV:CAC is 4.6x, CAC payback is 11.2 months and gross margin is 78.4%."},
        {label: "Efficiency", text: "Rule of 40 is 42.8% and burn multiple improved to 1.4x, indicating stronger capital efficiency."},
    ],
    arrBridge: {begin: 638, new: 36, expansion: 12, contraction: 7, churn: 12, end: 667, reconciled: true},
    metrics: [
        // ── Headline KPIs ──────────────────────────────────────────────
        metric({
            key: "arr",
            label: "ARR",
            group: "growth",
            format: "currency",
            raw: 8_000_000,
            display: "$8.00M",
            deltaPct: 4.5,
            basis: "vs prior month",
            headline: true,
            detail: {
                definition: "Annual Recurring Revenue — the annualised run-rate of all active recurring subscriptions.",
                method: "derived",
                formula: "ARR = MRR × 12",
                calculation: "$667K × 12 = $8.00M",
                inputs: [{label: "MRR", value: "$667K", sourceLabel: "Recurring Revenue", sourcePeriod: "Aug 2026"}],
                source: SYNTHETIC_SOURCE,
                basis: "Point-in-time, end of month",
                benchmark: "≈ $8M ARR is typical at Series A for this growth profile.",
                caveats: ["Assumes recurring revenue represents a normalised monthly run-rate."],
            },
        }),
        metric({
            key: "arr_growth",
            label: "ARR Growth",
            group: "growth",
            format: "percent",
            raw: 34.6,
            display: "34.6%",
            deltaPct: 2.5,
            basis: "year on year",
            headline: true,
            detail: {
                definition: "Year-over-year growth in Annual Recurring Revenue.",
                method: "derived",
                formula: "(ARR_now − ARR_12mo_ago) / ARR_12mo_ago",
                calculation: "($8.00M − $5.94M) / $5.94M = 34.6%",
                inputs: [
                    {label: "ARR — Aug 2026", value: "$8.00M", sourceLabel: "Derived (MRR × 12)", sourcePeriod: "Aug 2026"},
                    {label: "ARR — Aug 2025", value: "$5.94M", sourceLabel: "Derived (MRR × 12)", sourcePeriod: "Aug 2025"},
                ],
                source: SYNTHETIC_SOURCE,
                basis: "Trailing 12 months",
                benchmark: "Top-quartile Series A growth is 100%+; this cohort is scaling steadily.",
            },
        }),
        metric({
            key: "nrr",
            label: "Net Revenue Retention",
            group: "retention",
            format: "percent",
            raw: 111.8,
            display: "111.8%",
            deltaPct: -0.8,
            basis: "rolling TTM",
            headline: true,
            detail: {
                definition: "Revenue retained and expanded from the existing customer base, excluding new logos.",
                method: "imported",
                sourceRow: "Net Revenue Retention TTM",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "Rolling 12 months (TTM)",
                benchmark: "≥ 110% is best-in-class for SMB / mid-market SaaS.",
                caveats: [IMPORTED_NOTE],
            },
        }),
        metric({
            key: "grr",
            label: "Gross Revenue Retention",
            group: "retention",
            format: "percent",
            raw: 94.2,
            display: "94.2%",
            deltaPct: -0.3,
            basis: "rolling TTM",
            headline: true,
            detail: {
                definition: "Revenue retained from the existing base before expansion — measures pure churn resistance.",
                method: "imported",
                sourceRow: "Gross Revenue Retention TTM",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "Rolling 12 months (TTM)",
                benchmark: "≥ 90% signals durable retention.",
                caveats: [IMPORTED_NOTE],
            },
        }),
        metric({
            key: "ltv_cac",
            label: "LTV : CAC",
            group: "unit_economics",
            format: "ratio",
            raw: 4.6,
            display: "4.6x",
            deltaPct: 2.2,
            basis: "target ≥ 3.0x",
            headline: true,
            detail: {
                definition: "Lifetime value of a customer relative to the cost to acquire them — capital efficiency of growth.",
                method: "derived",
                formula: "LTV / CAC",
                calculation: "$38.6K / $8.4K = 4.6x",
                inputs: [
                    {label: "LTV (gross-margin laden)", value: "$38.6K", sourceLabel: "LTV (GM Laden)", sourcePeriod: "TTM"},
                    {label: "CAC (blended)", value: "$8.4K", sourceLabel: "CAC", sourcePeriod: "TTM"},
                ],
                source: SYNTHETIC_SOURCE,
                basis: "Rolling 12 months (TTM)",
                benchmark: "≥ 3.0x is healthy; > 5.0x may signal under-investment in growth.",
            },
        }),
        metric({
            key: "rule_of_40",
            label: "Rule of 40",
            group: "efficiency",
            format: "percent",
            raw: 42.8,
            display: "42.8%",
            deltaPct: 0.7,
            basis: "growth + margin",
            headline: true,
            detail: {
                definition: "Combined efficiency score: revenue growth rate plus profit margin should exceed 40%.",
                method: "imported",
                sourceRow: "Rule of 40 TTM",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "Rolling 12 months (TTM)",
                benchmark: "≥ 40% is the target; balances growth against burn.",
                caveats: [
                    IMPORTED_NOTE,
                    "Source uses growth + FCF-margin; confirm the margin variant before relying on this in a board pack.",
                ],
            },
        }),
        // ── Supporting (operating) metrics — not headline cards ─────────
        metric({
            key: "mrr",
            label: "MRR",
            group: "growth",
            format: "currency",
            raw: 667_000,
            display: "$667K",
            deltaPct: 4.5,
            basis: "Monthly run-rate",
            detail: {
                definition: "Monthly Recurring Revenue — normalised monthly value of active subscriptions.",
                method: "imported",
                sourceRow: "Recurring Revenue",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "End of month",
                caveats: [IMPORTED_NOTE, "Blends subscription and normalised usage-based revenue."],
            },
        }),
        metric({
            key: "total_revenue",
            label: "Total Revenue",
            group: "growth",
            format: "currency",
            raw: 812_000,
            display: "$812K",
            deltaPct: 4.2,
            basis: "This month",
            detail: {
                definition: "All revenue recognised in the month, including recurring and non-recurring.",
                method: "imported",
                sourceRow: "Total Revenue",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "Current month",
                caveats: [IMPORTED_NOTE],
            },
        }),
        metric({
            key: "recurring_pct",
            label: "Recurring Revenue",
            group: "growth",
            format: "percent",
            raw: 82.1,
            display: "82.1%",
            deltaPct: 0.3,
            basis: "Share of total",
            detail: {
                definition: "Share of total revenue that is recurring — quality-of-revenue indicator.",
                method: "derived",
                formula: "Recurring Revenue / Total Revenue",
                calculation: "$667K / $812K = 82.1%",
                inputs: [
                    {label: "Recurring revenue", value: "$667K", sourceLabel: "Recurring Revenue", sourcePeriod: "Aug 2026"},
                    {label: "Total revenue", value: "$812K", sourceLabel: "Total Revenue", sourcePeriod: "Aug 2026"},
                ],
                source: SYNTHETIC_SOURCE,
                basis: "Current month",
            },
        }),
        metric({
            key: "net_new",
            label: "Net New Revenue",
            group: "growth",
            format: "currency",
            raw: 29_000,
            display: "$29K",
            deltaPct: 11.5,
            basis: "This month",
            detail: {
                definition: "Net change in MRR this month across new, expansion, contraction and churn.",
                method: "derived",
                formula: "New + Expansion − Contraction − Churn",
                calculation: "$36K + $12K − $7K − $12K = $29K",
                inputs: [
                    {label: "New", value: "+$36K", sourceLabel: "ARR Bridge", sourcePeriod: "Aug 2026"},
                    {label: "Expansion", value: "+$12K", sourceLabel: "ARR Bridge", sourcePeriod: "Aug 2026"},
                    {label: "Contraction", value: "−$7K", sourceLabel: "ARR Bridge", sourcePeriod: "Aug 2026"},
                    {label: "Churn", value: "−$12K", sourceLabel: "ARR Bridge", sourcePeriod: "Aug 2026"},
                ],
                source: SYNTHETIC_SOURCE,
                basis: "Current month",
            },
        }),
        metric({
            key: "logo_retention",
            label: "Logo Retention",
            group: "retention",
            format: "percent",
            raw: 91.5,
            display: "91.5%",
            deltaPct: -0.3,
            basis: "Rolling TTM",
            detail: {
                definition: "Percentage of customers (by count) retained over the trailing year.",
                method: "imported",
                sourceRow: "Logo Retention TTM",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "Rolling 12 months (TTM)",
                caveats: [IMPORTED_NOTE],
            },
        }),
        metric({
            key: "gross_rev_churn",
            label: "Gross Revenue Churn",
            group: "retention",
            format: "percent",
            raw: 5.8,
            display: "5.8%",
            deltaPct: 0.3,
            basis: "Rolling TTM",
            inverse: true,
            detail: {
                definition: "Revenue lost to downgrades and cancellations, before any expansion.",
                method: "imported",
                sourceRow: "Gross Revenue Churn TTM",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "Rolling 12 months (TTM)",
                benchmark: "Lower is better; < 8% is healthy.",
                caveats: [IMPORTED_NOTE],
            },
        }),
        metric({
            key: "cac",
            label: "CAC",
            group: "unit_economics",
            format: "currency",
            raw: 8_400,
            display: "$8.4K",
            deltaPct: -2.3,
            basis: "All customers",
            inverse: true,
            detail: {
                definition: "Customer Acquisition Cost — fully-loaded sales & marketing spend per new customer.",
                method: "imported",
                sourceRow: "CAC",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "Rolling 12 months (TTM)",
                benchmark: "Falling CAC alongside rising ARPA signals improving efficiency.",
                caveats: [IMPORTED_NOTE],
            },
        }),
        metric({
            key: "ltv",
            label: "LTV",
            group: "unit_economics",
            format: "currency",
            raw: 38_600,
            display: "$38.6K",
            deltaPct: 0.5,
            basis: "Gross-margin laden",
            detail: {
                definition: "Lifetime value of a customer, gross-margin adjusted.",
                method: "derived",
                formula: "ARPA × Gross Margin / Gross Revenue Churn",
                calculation: "$7.1K × 78.4% / 5.8% ≈ $38.6K",
                inputs: [
                    {label: "ARPA", value: "$7.1K", sourceLabel: "ARPA", sourcePeriod: "TTM"},
                    {label: "Gross margin", value: "78.4%", sourceLabel: "Gross Margin", sourcePeriod: "TTM"},
                    {label: "Gross revenue churn", value: "5.8%", sourceLabel: "Gross Revenue Churn TTM", sourcePeriod: "TTM"},
                ],
                source: SYNTHETIC_SOURCE,
                basis: "Rolling 12 months (TTM)",
            },
        }),
        metric({
            key: "arpa",
            label: "ARPA",
            group: "unit_economics",
            format: "currency",
            raw: 7_100,
            display: "$7.1K",
            deltaPct: 1.4,
            basis: "Rolling TTM",
            detail: {
                definition: "Average Revenue Per Account — annualised revenue per active customer.",
                method: "derived",
                formula: "ARR / Active customers",
                calculation: "$8.00M / 94 ≈ $7.1K … annualised per account",
                inputs: [
                    {label: "ARR", value: "$8.00M", sourceLabel: "Derived (MRR × 12)", sourcePeriod: "Aug 2026"},
                    {label: "Active customers", value: "94", sourceLabel: "Active Customers", sourcePeriod: "Aug 2026"},
                ],
                source: SYNTHETIC_SOURCE,
                basis: "Rolling 12 months (TTM)",
            },
        }),
        metric({
            key: "cac_payback",
            label: "CAC Payback",
            group: "unit_economics",
            format: "months",
            raw: 11.2,
            display: "11.2 mo",
            deltaPct: -3.4,
            basis: "Rolling TTM",
            inverse: true,
            detail: {
                definition: "Months of gross profit required to recover the cost of acquiring a customer.",
                method: "derived",
                formula: "CAC / (ARPA / 12 × Gross Margin)",
                calculation: "$8.4K / ($7.1K / 12 × 78.4%) ≈ 11.2 months",
                inputs: [
                    {label: "CAC", value: "$8.4K", sourceLabel: "CAC", sourcePeriod: "TTM"},
                    {label: "ARPA", value: "$7.1K", sourceLabel: "ARPA", sourcePeriod: "TTM"},
                    {label: "Gross margin", value: "78.4%", sourceLabel: "Gross Margin", sourcePeriod: "TTM"},
                ],
                source: SYNTHETIC_SOURCE,
                basis: "Rolling 12 months (TTM)",
                benchmark: "< 12 months is efficient for this segment.",
            },
        }),
        metric({
            key: "gross_margin",
            label: "Gross Margin",
            group: "unit_economics",
            format: "percent",
            raw: 78.4,
            display: "78.4%",
            deltaPct: 0.4,
            basis: "Rolling TTM",
            detail: {
                definition: "Gross profit as a percentage of revenue, after cost of delivering the service.",
                method: "imported",
                sourceRow: "Gross Margin",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "Rolling 12 months (TTM)",
                benchmark: "AI-heavy inference COGS can pull margin below classic 80%+ SaaS.",
                caveats: [IMPORTED_NOTE],
            },
        }),
        metric({
            key: "burn_multiple",
            label: "Burn Multiple",
            group: "efficiency",
            format: "ratio",
            raw: 1.4,
            display: "1.4x",
            deltaPct: -6.7,
            basis: "Rolling TTM",
            inverse: true,
            detail: {
                definition: "Net cash burned per dollar of net new ARR added — capital efficiency of growth.",
                method: "imported",
                sourceRow: "Burn Multiple TTM",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "Rolling 12 months (TTM)",
                benchmark: "< 1.5x is good; < 1.0x is excellent.",
                caveats: [
                    IMPORTED_NOTE,
                    "Depends on the net-burn definition in the source; confirm whether it is operating or all-in burn.",
                ],
            },
        }),
        metric({
            key: "magic_number",
            label: "Magic Number",
            group: "efficiency",
            format: "ratio",
            raw: 0.9,
            display: "0.90x",
            deltaPct: 2.3,
            basis: "Sales efficiency",
            detail: {
                definition: "New ARR generated per dollar of prior-period sales & marketing spend.",
                method: "imported",
                sourceRow: "Magic Number",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "Quarterly",
                benchmark: "> 0.75x justifies further S&M investment.",
                caveats: [
                    IMPORTED_NOTE,
                    "Source row was sparse in the reference snapshot; treat as indicative until the definition is confirmed.",
                ],
            },
        }),
        metric({
            key: "customers",
            label: "Customers",
            group: "efficiency",
            format: "count",
            raw: 94,
            display: "94",
            deltaPct: 3.3,
            basis: "Active customers",
            detail: {
                definition: "Active recurring-revenue customers at month end.",
                method: "imported",
                sourceRow: "Active Customers",
                source: SYNTHETIC_SOURCE,
                importedAt: IMPORTED_AT,
                schemaVersion: SCHEMA_VERSION,
                basis: "End of month",
                caveats: [IMPORTED_NOTE],
            },
        }),
    ],
};

// ── Jul & Jun snapshots reuse the Aug structure/detail with adjusted values ─

const JUL_2026: MonthSnapshot = adjustSnapshot(AUG_2026, {
    asOf: "2026-07",
    label: "Jul 2026",
    previousLabel: "Jun",
    periodSubtitle: "Board view · Jul 2026 · rolling 12-month metrics where noted",
    arrBridge: {begin: 612, new: 33, expansion: 11, contraction: 6, churn: 12, end: 638, reconciled: true},
    overrides: {
        arr: {raw: 7_660_000, display: "$7.66M"},
        arr_growth: {raw: 32.1, display: "32.1%"},
        nrr: {raw: 112.6, display: "112.6%"},
        grr: {raw: 94.5, display: "94.5%"},
        ltv_cac: {raw: 4.5, display: "4.5x"},
        rule_of_40: {raw: 42.1, display: "42.1%"},
        mrr: {raw: 638_000, display: "$638K"},
        total_revenue: {raw: 779_000, display: "$779K"},
        recurring_pct: {raw: 81.9, display: "81.9%"},
        net_new: {raw: 26_000, display: "$26K"},
        logo_retention: {raw: 91.8, display: "91.8%"},
        gross_rev_churn: {raw: 5.5, display: "5.5%"},
        cac: {raw: 8_600, display: "$8.6K"},
        ltv: {raw: 38_400, display: "$38.4K"},
        arpa: {raw: 7_000, display: "$7.0K"},
        cac_payback: {raw: 11.6, display: "11.6 mo"},
        gross_margin: {raw: 78.0, display: "78.0%"},
        burn_multiple: {raw: 1.5, display: "1.5x"},
        magic_number: {raw: 0.88, display: "0.88x"},
        customers: {raw: 91, display: "91"},
    },
    executiveSummary: [
        {label: "Growth", text: "ARR reached $7.66M, up 32.1% year on year, with $26K of net-new MRR this month."},
        {label: "Retention", text: "NRR is strong at 112.6% and GRR at 94.5%, with expansion outpacing churn."},
        {label: "Unit economics", text: "LTV:CAC is 4.5x, CAC payback is 11.6 months and gross margin is 78.0%."},
        {label: "Efficiency", text: "Rule of 40 is 42.1% and burn multiple is 1.5x."},
    ],
});

const JUN_2026: MonthSnapshot = adjustSnapshot(AUG_2026, {
    asOf: "2026-06",
    label: "Jun 2026",
    previousLabel: "May",
    periodSubtitle: "Board view · Jun 2026 · rolling 12-month metrics where noted",
    arrBridge: {begin: 586, new: 34, expansion: 10, contraction: 6, churn: 12, end: 612, reconciled: true},
    overrides: {
        arr: {raw: 7_340_000, display: "$7.34M"},
        arr_growth: {raw: 29.8, display: "29.8%"},
        nrr: {raw: 111.1, display: "111.1%"},
        grr: {raw: 93.8, display: "93.8%"},
        ltv_cac: {raw: 4.3, display: "4.3x"},
        rule_of_40: {raw: 40.8, display: "40.8%"},
        mrr: {raw: 612_000, display: "$612K"},
        total_revenue: {raw: 748_000, display: "$748K"},
        recurring_pct: {raw: 81.8, display: "81.8%"},
        net_new: {raw: 26_000, display: "$26K"},
        logo_retention: {raw: 91.0, display: "91.0%"},
        gross_rev_churn: {raw: 6.2, display: "6.2%"},
        cac: {raw: 8_700, display: "$8.7K"},
        ltv: {raw: 37_700, display: "$37.7K"},
        arpa: {raw: 6_900, display: "$6.9K"},
        cac_payback: {raw: 12.1, display: "12.1 mo"},
        gross_margin: {raw: 77.6, display: "77.6%"},
        burn_multiple: {raw: 1.6, display: "1.6x"},
        magic_number: {raw: 0.84, display: "0.84x"},
        customers: {raw: 88, display: "88"},
    },
    executiveSummary: [
        {label: "Growth", text: "ARR reached $7.34M, up 29.8% year on year, with $26K of net-new MRR this month."},
        {label: "Retention", text: "NRR is 111.1% and GRR 93.8%; retention remains above target."},
        {label: "Unit economics", text: "LTV:CAC is 4.3x, CAC payback is 12.1 months and gross margin is 77.6%."},
        {label: "Efficiency", text: "Rule of 40 is 40.8% and burn multiple is 1.6x."},
    ],
});

/** Builds a month snapshot by cloning a base and overriding per-metric raw/display. */
function adjustSnapshot(
    base: MonthSnapshot,
    patch: {
        asOf: string;
        label: string;
        previousLabel: string;
        periodSubtitle: string;
        arrBridge: ArrBridge;
        executiveSummary: {label: string; text: string}[];
        overrides: Record<string, {raw: number; display: string}>;
    },
): MonthSnapshot {
    return {
        asOf: patch.asOf,
        label: patch.label,
        previousLabel: patch.previousLabel,
        periodSubtitle: patch.periodSubtitle,
        executiveSummary: patch.executiveSummary,
        arrBridge: patch.arrBridge,
        metrics: base.metrics.map((m) => {
            const o = patch.overrides[m.key];
            return o ? {...m, raw: o.raw, display: o.display} : m;
        }),
    };
}

// ─────────────────────────────────────────────────────────────────────────
// Trend series (12 months) + scorecard (last 6 months)
// ─────────────────────────────────────────────────────────────────────────

const TREND: TrendPoint[] = [
    {monthKey: "2025-09", label: "Sep", revenue: 512, arr: 5.94, nrr: 108.5, grr: 92.8, customers: 71},
    {monthKey: "2025-10", label: "Oct", revenue: 528, arr: 6.11, nrr: 108.9, grr: 93.0, customers: 73},
    {monthKey: "2025-11", label: "Nov", revenue: 551, arr: 6.34, nrr: 109.4, grr: 93.2, customers: 75},
    {monthKey: "2025-12", label: "Dec", revenue: 579, arr: 6.58, nrr: 110.1, grr: 93.5, customers: 78},
    {monthKey: "2026-01", label: "Jan", revenue: 603, arr: 6.79, nrr: 109.8, grr: 93.4, customers: 80},
    {monthKey: "2026-02", label: "Feb", revenue: 631, arr: 7.02, nrr: 110.6, grr: 93.6, customers: 82},
    {monthKey: "2026-03", label: "Mar", revenue: 662, arr: 6.49, nrr: 108.7, grr: 93.3, customers: 84},
    {monthKey: "2026-04", label: "Apr", revenue: 691, arr: 6.76, nrr: 109.3, grr: 93.7, customers: 85},
    {monthKey: "2026-05", label: "May", revenue: 718, arr: 7.03, nrr: 110.2, grr: 94.0, customers: 86},
    {monthKey: "2026-06", label: "Jun", revenue: 748, arr: 7.34, nrr: 111.1, grr: 93.8, customers: 88},
    {monthKey: "2026-07", label: "Jul", revenue: 779, arr: 7.66, nrr: 112.6, grr: 94.5, customers: 91},
    {monthKey: "2026-08", label: "Aug", revenue: 812, arr: 8.0, nrr: 111.8, grr: 94.2, customers: 94},
];

const SCORECARD_MONTHS = ["Mar 2026", "Apr 2026", "May 2026", "Jun 2026", "Jul 2026", "Aug 2026"];

const SCORECARD: ScorecardRow[] = [
    {metric: "MRR", format: "currency", values: [541_000, 563_000, 586_000, 612_000, 638_000, 667_000]},
    {metric: "Net New Revenue", format: "currency", values: [21_000, 22_000, 23_000, 26_000, 26_000, 29_000]},
    {metric: "NRR (TTM)", format: "percent", values: [108.7, 109.3, 110.2, 111.1, 112.6, 111.8]},
    {metric: "GRR (TTM)", format: "percent", values: [93.3, 93.7, 94.0, 93.8, 94.5, 94.2]},
    {metric: "CAC", format: "currency", values: [9_300, 9_000, 8_800, 8_700, 8_600, 8_400], inverse: true},
    {metric: "LTV:CAC", format: "ratio", values: [3.8, 4.0, 4.1, 4.3, 4.5, 4.6]},
    {metric: "Gross Margin", format: "percent", values: [76.2, 76.8, 77.1, 77.6, 78.0, 78.4]},
    {metric: "Rule of 40", format: "percent", values: [35.4, 37.1, 39.6, 40.8, 42.1, 42.8]},
    {metric: "Burn Multiple", format: "ratio", values: [2.1, 1.9, 1.8, 1.6, 1.5, 1.4], inverse: true},
];

// ─────────────────────────────────────────────────────────────────────────

export const SAAS_SUMMARY_DATA: SaaSSummaryData = {
    source: "Synthetic snapshot",
    availableMonths: [
        {value: "2026-08", label: "Aug 2026"},
        {value: "2026-07", label: "Jul 2026"},
        {value: "2026-06", label: "Jun 2026"},
    ],
    months: {
        "2026-08": AUG_2026,
        "2026-07": JUL_2026,
        "2026-06": JUN_2026,
    },
    trend: TREND,
    scorecardMonths: SCORECARD_MONTHS,
    scorecard: SCORECARD,
};

export const DEFAULT_AS_OF = "2026-08";

/** Convenience: metric group → display label. */
export const METRIC_GROUP_LABELS: Record<MetricGroup, string> = {
    growth: "Growth",
    retention: "Retention",
    unit_economics: "Unit economics",
    efficiency: "Efficiency",
};
